import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/cn";

interface Props {
  page: number;
  pageSize: number;
  total?: number;
  onPageChange: (p: number) => void;
  onPageSizeChange?: (s: number) => void;
}

export function PaginationBar({ page, pageSize, total, onPageChange, onPageSizeChange }: Props) {
  const totalPages = total !== undefined ? Math.max(1, Math.ceil(total / pageSize)) : undefined;
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total ?? page * pageSize);

  return (
    <div className="flex items-center justify-between border-t bg-card px-4 py-2 text-sm">
      <div className="text-muted-foreground">
        {total !== undefined ? (
          <>
            {from} – {to} of <span className="font-medium text-foreground">{total.toLocaleString()}</span>
          </>
        ) : (
          <>Loading…</>
        )}
      </div>

      <div className="flex items-center gap-2">
        {onPageSizeChange && (
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="h-8 rounded-md border bg-background px-2 text-sm"
          >
            {[25, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
          </select>
        )}

        <div className="flex overflow-hidden rounded-md border">
          <button
            aria-label="Previous page"
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page === 1}
            className={cn("flex h-8 w-8 items-center justify-center hover:bg-muted", page === 1 && "opacity-40")}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="flex min-w-[5rem] items-center justify-center border-l border-r px-3 text-xs tabular-nums">
            Page {page}{totalPages ? ` of ${totalPages}` : ""}
          </div>
          <button
            aria-label="Next page"
            onClick={() => onPageChange(page + 1)}
            disabled={totalPages ? page >= totalPages : false}
            className={cn("flex h-8 w-8 items-center justify-center hover:bg-muted", totalPages && page >= totalPages && "opacity-40")}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
